import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScreenSize } from '@/hooks';
import { storage } from '@/utils';
import { Wallet, Gift, ArrowRight } from 'lucide-react';
import Button from '@/components/common/Button/Button';
import Modal from '@/components/common/Modal/Modal';
import styles from './Bonus500Banner.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';

/**
 * Bonus500Banner Component
 * Displays the +100% first deposit bonus banner
 * Matches theme from variables.css
 */
const Bonus500Banner = () => {
  const navigate = useNavigate();
  const { isMobile } = useScreenSize();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleGetBonus = () => {
    // Navigate to Gift Cards page
    navigate('/gift-cards');
  };

  const handleInfoClick = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleReceiveBonus = () => {
    setIsModalOpen(false);
    
    // Check if user is logged in
    const isLoggedIn = storage.getKey('logged') === true || localStorage.getItem('logged') === 'true';
    
    if (isLoggedIn) {
      // Open wallet modal with deposit tab
      navigate('/wallet?mode=deposit');
    } else {
      // Open auth modal for signin
      navigate('/login?modalType=auth-signin');
    }
  };

  return (
    <div className={styles.root}>
      <div className={`${styles.container} ${styles.style4}`}>
        <div className={styles.content}>
          {/* Title Section */}
          <div className={styles.title}>
            <div className={styles.titleContent}>
              Gift Card Vouchers
            </div>
            <div
              className={styles.icon}
              onClick={handleInfoClick}
              aria-label="View gift card details"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleInfoClick();
                }
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" role="presentation" width="24" height="24" data-icon="inform">
                <path fill="currentColor" fillRule="evenodd" d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm.566-14.435a.8.8 0 1 1-1.132 1.131.8.8 0 0 1 1.132-1.131zm.234 8.304a.8.8 0 1 1-1.6 0V9.276a.8.8 0 0 1 1.6 0v4.593z" clipRule="evenodd"></path>
              </svg>
            </div>
          </div>

          {/* Steps Section */}
          <div className={styles.steps}>
            <div className={styles.stepItem}>
              <div className={styles.stepPosition}>
              <FontAwesomeIcon
              icon={faAngleRight}
              // className={styles.doubleArrowRight}
              style={{ fontSize: '20px' }}
            />
               {/* <FontAwesomeIcon
              icon={faAngleRight}
              // className={styles.doubleArrowRight}
              style={{ marginLeft: '-4px', fontSize: '20px' }}
            /> */}
              </div>
              <div className={styles.stepInfo}>Activate with unique codes</div>
            </div>
            <div className={styles.stepItem}>
              <div className={styles.stepPosition}>
                {/* <Gift size={14} strokeWidth={2.5} color="black" /> */}
                <FontAwesomeIcon
              icon={faAngleRight}
              // className={styles.doubleArrowRight}
              style={{ fontSize: '20px' }}
            />
               {/* <FontAwesomeIcon
              icon={faAngleRight}
              // style={{marginLeft: '-2px'}}
              // className={styles.doubleArrowRight}
              style={{ marginLeft: '-4px', fontSize: '20px' }}
            /> */}
              </div>
              <div className={styles.stepInfo}>Claim rewards instantly</div>
            </div>
          </div>

          {/* Button Section */}
          <div className={styles.buttonWrapper}>
            <Button
              variant="secondary"
              size="lg"
              onClick={handleGetBonus}
              className={styles.bonusButton}
            >
              View Gift Cards
            </Button>
          </div>
        </div>

        {/* Bonus Icon/Image Section */}
        <div className={styles.image}>
          <picture>
            {isMobile ? (
              <>
                <source srcSet="/assets/images/mobile-bonus.avif" type="image/avif" />
                <img
                  src="/assets/images/mobile-bonus.avif"
                  alt="Bonus gift box"
                  className={styles.bannerImg}
                  loading="lazy"
                />
              </>
            ) : (
              <>
                <source srcSet="/assets/images/bonus.avif" type="image/avif" />
                <img
                  src="/assets/images/bonus.webp"
                  alt="Bonus gift box"
                  className={styles.bannerImg}
                  loading="lazy"
                />
              </>
            )}
          </picture>
        </div>
      </div>

      {/* Gift Card Vouchers Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title="Gift Card Vouchers"
        size="md"
      >
        <div className={styles.modalContent}>
          <div className={styles.modalSubtitle}>Activate your gift card vouchers with unique codes</div>

          {/* Gift Card Voucher Section - First */}
          <div className={styles.modalSection}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionIcon}>
                <Gift size={20} />
              </div>
              <h3 className={styles.sectionTitle}>Gift Card Vouchers</h3>
            </div>
            <div className={styles.sectionDetails}>
              <p className={styles.voucherDescription}>
                Activate your gift card vouchers with unique codes. Complete wager requirements and claim your rewards to boost your wallet balance.
              </p>
              <div className={styles.voucherFeatures}>
                <div className={styles.voucherFeature}>
                  <span className={styles.featureIcon}>✓</span>
                  <span>Activate with unique codes</span>
                </div>
                <div className={styles.voucherFeature}>
                  <span className={styles.featureIcon}>✓</span>
                  <span>Track wager requirements</span>
                </div>
                <div className={styles.voucherFeature}>
                  <span className={styles.featureIcon}>✓</span>
                  <span>Claim rewards instantly</span>
                </div>
              </div>
              <Button
                variant="secondary"
                size="md"
                onClick={() => {
                  handleModalClose();
                  navigate('/gift-cards');
                }}
                className={styles.viewGiftCardsButton}
              >
                View Gift Cards
                <ArrowRight size={16} style={{ marginLeft: '8px' }} />
              </Button>
            </div>
          </div>

          {/* Gift Card Voucher Section */}
          <div className={styles.modalSection}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionIcon}>
                <Gift size={20} />
              </div>
              <h3 className={styles.sectionTitle}>Gift Card Vouchers</h3>
            </div>
            <div className={styles.sectionDetails}>
              <p className={styles.voucherDescription}>
                Activate your gift card vouchers with unique codes. Complete wager requirements and claim your rewards to boost your wallet balance.
              </p>
              <div className={styles.voucherFeatures}>
                <div className={styles.voucherFeature}>
                  <span className={styles.featureIcon}>✓</span>
                  <span>Activate with unique codes</span>
                </div>
                <div className={styles.voucherFeature}>
                  <span className={styles.featureIcon}>✓</span>
                  <span>Track wager requirements</span>
                </div>
                <div className={styles.voucherFeature}>
                  <span className={styles.featureIcon}>✓</span>
                  <span>Claim rewards instantly</span>
                </div>
              </div>
              <Button
                variant="secondary"
                size="md"
                onClick={() => {
                  handleModalClose();
                  navigate('/gift-cards');
                }}
                className={styles.viewGiftCardsButton}
              >
                View Gift Cards
                <ArrowRight size={16} style={{ marginLeft: '8px' }} />
              </Button>
            </div>
          </div>

          {/* Receive Button */}
          <div className={styles.modalButtonWrapper}>
            <Button
              variant="primary"
              size="lg"
              onClick={handleReceiveBonus}
              className={styles.receiveButton}
            >
              Receive
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Bonus500Banner;
